import { get, isEmpty } from "lodash";
import __ from "src/utils/Translations";
import {
  BillingCyclePattern,
  EPatternUnit,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IEmployeePatternAssignments } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

const getPatternLabel = (unit: EPatternUnit, value: number) => {
  if (unit === EPatternUnit.Day) {
    return `${value} ${__("dni")}`;
  }

  if (unit === EPatternUnit.Hour) {
    return `${value} ${__("godziny")}`;
  }

  return "";
};

const formatPatternsToOptions = (patterns: BillingCyclePattern[]) => {
  if (!patterns || isEmpty(patterns)) return [];

  return patterns
    .filter((pattern: BillingCyclePattern) => pattern?.id)
    .map((pattern: BillingCyclePattern) => ({
      id: pattern.id,
      label: getPatternLabel(EPatternUnit.Day, pattern.value),
    }));
};

/**
 * Function filtering out inactive absence types and transforming default API data to structure ready to use in MUI Autocomplete.
 * @param {array} absenceTypes - collection of absence types from API.
 */
export const transformAbsenceTypesToOptions = (
  absenceTypes: IAbsenceType[]
) => {
  if (!absenceTypes || isEmpty(absenceTypes)) return [];

  return absenceTypes
    .filter(
      (type) =>
        !!type?.active &&
        type.billingCycleConfiguration &&
        !isEmpty(type.billingCycleConfiguration?.patterns)
    )
    .map((type) => ({
      absenceTypeId: type.id,
      label: type.name,
      patterns: type.billingCycleConfiguration?.patterns
        ? formatPatternsToOptions(type.billingCycleConfiguration.patterns)
        : [],
    }));
};

/**
 * Function filtering Absence Type autocomplete field options based on previous choices. If Absence Type is already selected, it's not visible.
 * @param {array} allOptions - collection of all available Absence Types in form of autocomplete options.
 * @param {array} selectedAbsenceTypeIds - already selected Absence Type IDs.
 * @param {number} currentIndex - form field index (fields are cloneable).
 * @param {string} currentValue - form field value.
 */
export const getAvailableAbsenceTypeOptions = (
  allOptions: ReturnType<typeof transformAbsenceTypesToOptions>,
  selectedAbsenceTypeIds: string[],
  currentIndex: number,
  currentValue: string
) =>
  allOptions
    .filter((option) => {
      if (option.absenceTypeId === currentValue) {
        return true;
      }
      return !selectedAbsenceTypeIds.some(
        (id, idx) => idx !== currentIndex && id === option.absenceTypeId
      );
    })
    .map((option) => ({
      value: option.absenceTypeId,
      label: option.label,
    }));

/**
 * Function transforming API Absence Type billing cycle data to structure ready to use in hook-form default values.
 * @param {array} patternsData - collection of patterns from API.
 */
export const getInitialPatterns = (
  patternsData: IEmployeePatternAssignments
) => {
  const patternAssignments = get(patternsData, "patternAssignments", []);
  if (!isEmpty(patternAssignments)) {
    return patternAssignments.map((assignment) => ({
      absenceTypeId: assignment.absenceTypeId || "",
      patternId: assignment.patternId || "",
    }));
  }
  return [{ absenceTypeId: "", patternId: "" }];
};
